import __ from '../../utils/translation';
import { getCookie, setCookie } from '../../utils/cookies';

const COOKIE_NAME = 'download_terms_accepted';
const COOKIE_EXPIRY_DAYS = 1;

interface TermsModalOptions {
  termsText: string;
  onAccept: () => void;
  onDecline: () => void;
}

class TermsModal {
  private modal: HTMLElement | null = null;

  private termsContent: HTMLElement | null = null;

  private agreeButton: HTMLButtonElement | null = null;

  private declineButton: HTMLButtonElement | null = null;

  private onAccept: (() => void) | null = null;

  private onDecline: (() => void) | null = null;

  /**
   * Check if terms have been accepted
   */
  public hasAcceptedTerms(): boolean {
    return getCookie(COOKIE_NAME) === 'true';
  }

  /**
   * Show the terms modal
   */
  public show(options: TermsModalOptions): void {
    this.onAccept = options.onAccept;
    this.onDecline = options.onDecline;

    this.createModal(options.termsText);
    this.attachEventListeners();
  }

  /**
   * Create the modal HTML structure
   */
  private createModal(termsText: string): void {
    const modalHTML = `
      <div class="denso-modal denso-modal_scrollable" data-terms-modal>
        <div class="denso-modal__bg" aria-hidden="true" data-terms-decline></div>
        <div class="denso-modal__content denso-modal__content_terms">
          <div class="denso-modal__close">
            <button
              type="button"
              class="close-button denso-modal__close-btn"
              data-terms-decline
            >
              <svg class="close-button__icon">
                <use xlink:href="/dist/images/denso-sprite.svg#cross" />
              </svg>
            </button>
          </div>
          <div class="denso-modal__content-inner">
            <div class="denso-modal__terms-content" data-terms-content>
              ${termsText}
            </div>
            <div class="denso-modal__terms-actions">
              <button
                type="button"
                class="button denso-modal__terms-btn"
                data-terms-agree
                disabled
              >
                ${__('Agree')}
              </button>
              <button
                type="button"
                class="button button_outline denso-modal__terms-btn"
                data-terms-decline
              >
                ${__('Decline')}
              </button>
            </div>
          </div>
        </div>
      </div>
    `;

    document.body.insertAdjacentHTML('beforeend', modalHTML);

    this.modal = document.querySelector('[data-terms-modal]');
    this.termsContent = document.querySelector('[data-terms-content]');
    this.agreeButton = document.querySelector('[data-terms-agree]');
    this.declineButton = document.querySelector('[data-terms-decline]');
  }

  /**
   * Attach event listeners
   */
  private attachEventListeners(): void {
    if (!this.modal || !this.termsContent || !this.agreeButton || !this.declineButton) {
      return;
    }

    // Scroll detection to enable Agree button
    this.termsContent.addEventListener('scroll', this.handleScroll.bind(this));

    // Agree button
    this.agreeButton.addEventListener('click', this.handleAgree.bind(this));

    // Decline button (works for both button and close actions)
    const declineElements = this.modal.querySelectorAll('[data-terms-decline]');
    declineElements.forEach((element) => {
      element.addEventListener('click', this.handleDecline.bind(this));
    });

    // Check if content is already scrolled to bottom (for short content)
    this.checkIfScrolledToBottom();
  }

  /**
   * Check if user has scrolled to the bottom
   */
  private handleScroll(): void {
    this.checkIfScrolledToBottom();
  }

  /**
   * Check if content is scrolled to bottom and enable Agree button
   */
  private checkIfScrolledToBottom(): void {
    if (!this.termsContent || !this.agreeButton) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = this.termsContent;
    const isScrolledToBottom = scrollTop + clientHeight >= scrollHeight - 10; // 10px threshold

    if (isScrolledToBottom) {
      this.agreeButton.disabled = false;
    }
  }

  /**
   * Handle Agree button click
   */
  private handleAgree(): void {
    setCookie(COOKIE_NAME, 'true', COOKIE_EXPIRY_DAYS);

    // Call onAccept BEFORE destroying the modal
    if (this.onAccept) {
      this.onAccept();
    }

    this.destroy();
  }

  /**
   * Handle Decline button click
   */
  private handleDecline(): void {
    // Call onDecline BEFORE destroying the modal
    if (this.onDecline) {
      this.onDecline();
    }

    this.destroy();
  }

  /**
   * Destroy the modal
   */
  private destroy(): void {
    if (this.modal) {
      this.modal.remove();
      this.modal = null;
      this.termsContent = null;
      this.agreeButton = null;
      this.declineButton = null;
      this.onAccept = null;
      this.onDecline = null;
    }
  }
}

export default TermsModal;
